import { useCallback } from 'react';
import {
  useCsrfContext,
  getGlobalCsrfToken as getGlobalToken,
  getCachedCsrfToken as getCachedToken,
} from '../providers/CsrfProvider';

/**
 * CSRF token hook - now just a wrapper around CsrfProvider
 * 
 * This hook provides access to the globally initialized CSRF token
 * and methods for secure API calls.
 * 
 * Usage:
 * - For simple token access: const { csrfToken } = useCsrfToken();
 * - For secure fetch wrapper: const { secureFetch } = useCsrfToken();
 */
export function useCsrfToken() {
  const { csrfToken, isLoading, error } = useCsrfContext();

  // Secure fetch wrapper that includes CSRF token
  // NOTE: This callback has an empty dependency array intentionally to keep the
  // function reference stable across renders. This prevents useEffect hooks that
  // depend on secureFetch from re-running on every render.
  // We use getCachedToken() which reads from a stable global/cookie source.
  const secureFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    // Add CSRF token to headers for state-changing requests
    const method = options.method?.toUpperCase() || 'GET';
    // Always use getCachedToken() for the latest token value from global cache/cookie
    // This ensures we get the fresh token without requiring a dependency on csrfToken
    const effectiveToken = getCachedToken();

    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method) && effectiveToken) {
      options.headers = {
        ...options.headers,
        'X-CSRF-Token': effectiveToken,
      };
    }

    // Always include credentials for cookie-based auth
    options.credentials = options.credentials || 'include';

    return fetch(url, options);
  }, []);

  return {
    csrfToken,
    isLoading,
    error,
    fetchCsrfToken: () => Promise.resolve(csrfToken), // Compatibility shim
    secureFetch,
  };
}

// Alias for backward compatibility
export const useSecureCsrf = useCsrfToken;

// Re-export with original names for backward compatibility
export const getGlobalCsrfToken = getGlobalToken;
export const getCachedCsrfToken = getCachedToken;

// Helper for adding CSRF to headers
export function addCsrfToHeaders(headers: HeadersInit = {}): HeadersInit {
  const token = getCachedToken();
  if (token) {
    return {
      ...headers,
      'X-CSRF-Token': token,
    };
  }
  return headers;
}